// useWorkflowRunStream — Subscribes to workflow run frames via workflowFrameBus.
//
// Phase 03: Replaces per-run EventSource with multiplex hub subscription.
// Hook mounts → subscribes bus by runId → feeds local reducer → returns state.
// Public API UNCHANGED (8 consumer files compile without modification).
// StepStreamState shape FROZEN: additions OK, deletions FORBIDDEN (H5).
//
// EventSource lifecycle, fallback-polling, and reconnect timer removed.
// 5 dead event types removed (llm_call_start/complete, cost_tick, cost_cap_*):
//   BE never emits them; matching StepStreamState fields PRESERVED as nullable
//   (reserved — cost tracking deferred to future event flow).
//
// Reducer, INITIAL_STATE, StreamAction, and frame dispatcher live in:
//   useWorkflowRunStream-reducer.ts
import { useEffect, useReducer, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RunStatus, StepStatus, RunEvent, Checkpoint } from "@/types/workflow.types";
import { workflowFrameBus } from "@/lib/realtime/workflow-frame-bus";
import { useRealtime } from "@/lib/realtime/realtime-provider";
import type { Frame } from "@/lib/realtime/types";
import { reducer, INITIAL_STATE, dispatchFrameToReducer } from "./useWorkflowRunStream-reducer";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface StepStreamState {
  stepId: string;
  status: StepStatus;
  progressPercent: number;
  progressMessage?: string;
  inputCount?: number;
  outputCount?: number;
  durationMs?: number;
  errorMessage?: string;
  iterCount?: number;
  /** Committed final text — set by step_text_complete. */
  outputText?: string;
  /** Chosen branch label from decision_made. */
  decisionLabel?: string;
  /** Reasoning text from decision_made. */
  decisionReasoning?: string;
  /** Populated from step.completed payload; per-token cost stream deferred. */
  costUsd?: number;
}

export interface WorkflowRunStreamState {
  isConnected: boolean;
  runStatus: RunStatus | null;
  paused: boolean;
  pausedReason?: string;
  checkpointPending: Checkpoint | null;
  steps: Record<string, StepStreamState>;
  /** Per-step DB-persisted events (excludes token_delta). */
  eventsByStepId: Record<string, RunEvent[]>;
  /** All events list in arrival order (excludes token_delta). */
  allEvents: RunEvent[];
  /** Per-step accumulator for live token_delta text. Cleared on step_text_complete. */
  liveTokensByStepId: Record<string, string>;
  lastEventId: string | null;
  useFallbackPolling: boolean;
  terminated: boolean;
  error?: string;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useWorkflowRunStream(
  runId: string | null,
  _workflowId: string,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const [state, dispatchReducer] = useReducer(reducer, INITIAL_STATE);
  const { status: realtimeStatus, lastEventId: providerLastEventId } = useRealtime();

  // Track last-connected timestamp for REST fallback on >60s gap (Q17).
  const lastConnectedAtRef = useRef<number>(Date.now());

  // Derived connection flags from multiplex provider status.
  const isConnected = realtimeStatus === "open";
  const useFallbackPolling = realtimeStatus === "polling";

  // Sync state flags that depended on the old EventSource lifecycle.
  useEffect(() => {
    if (isConnected) {
      dispatchReducer({ type: "connected" });
    } else if (realtimeStatus === "offline") {
      dispatchReducer({ type: "disconnected" });
    }
  }, [isConnected, realtimeStatus]);

  // REST fallback on >60s disconnect gap (red-team Q17).
  useEffect(() => {
    if (!runId || !enabled) return;
    if (realtimeStatus === "open") {
      const now = Date.now();
      if (now - lastConnectedAtRef.current > 60_000) {
        queryClient.invalidateQueries({ queryKey: ["workflows", "run", runId, "events"] });
      }
      lastConnectedAtRef.current = now;
    }
    // Do not update lastConnectedAtRef on disconnect — preserve the disconnect timestamp.
  }, [realtimeStatus, runId, queryClient, enabled]);

  // Subscribe to workflowFrameBus by runId.
  useEffect(() => {
    if (!runId || !enabled) return;
    // Reset state when runId changes.
    dispatchReducer({ type: "reset" });
    lastConnectedAtRef.current = Date.now();
    const unsubscribe = workflowFrameBus.subscribe(runId, (frame: Frame) => {
      dispatchFrameToReducer(frame, dispatchReducer);
    });
    return unsubscribe;
  }, [runId, enabled]);

  const reset = useCallback(() => {
    dispatchReducer({ type: "reset" });
  }, []);

  // disconnect is kept for API stability — no-op in multiplex mode.
  const disconnect = useCallback(() => {
    dispatchReducer({ type: "disconnected" });
  }, []);

  return {
    ...state,
    isConnected,
    useFallbackPolling,
    // Provider's uint64 cursor — preserved for API stability; consumers may read it.
    lastEventId: state.lastEventId ?? String(providerLastEventId),
    disconnect,
    reset,
  };
}
